import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { getResumeEntries, deleteResumeEntry } from '../../api/api';
import ResumeModal from '../../components/ResumeModal/ResumeModal';
import { useAuthStore } from '../../store/authStore';
import Loader from '../../components/Loader/Loader';
import './Resume.css';
import '../Animation.css';

const Resume = () => {
  const { isAuthenticated } = useAuthStore();

  const [resumeData, setResumeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [editingEntry, setEditingEntry] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  useEffect(() => {
    const fetchResume = async () => {
      setLoading(true);

      try {
        const data = await getResumeEntries();
        setResumeData(data.sort((a, b) => a.sortOrder - b.sortOrder));
      } catch (err) {
        setError(err.message || 'Błąd pobierania resume');
        console.error('Błąd pobierania resume:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchResume();
  }, []);

  // grupowanie po kategorii
  const grouped = resumeData.reduce((acc, item) => {
    acc[item.category] = acc[item.category] || [];
    acc[item.category].push(item);
    return acc;
  }, {});

  Object.keys(grouped).forEach((key) => {
    grouped[key].sort((a, b) => a.sortOrder - b.sortOrder);
  });

  const renderMultilineText = (text) => {
    if (!text) return null;

    return text
      .replace(/\\n/g, '\n')
      .split('\n')
      .map((line, index) => <p key={index}>{line}</p>);
  };

  const openModal = (entry = null, mode = 'add', category = null) => {
    setEditingEntry(entry);
    setModalMode(mode);
    setSelectedCategory(category);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingEntry(null);
    setModalMode('add');
    setSelectedCategory(null);
  };

  const handleSave = (saved) => {
    if (editingEntry?.id) {
      setResumeData((prev) =>
        prev.map((item) => (item.id === saved.id ? saved : item))
      );
    } else {
      setResumeData((prev) =>
        [...prev, saved].sort((a, b) => a.sortOrder - b.sortOrder)
      );
    }
    closeModal();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Na pewno chcesz usunąć ten wpis?')) return;

    try {
      await deleteResumeEntry(id);
      setResumeData((prev) => prev.filter((item) => item.id !== id));
      alert('Wpis usunięty pomyślnie');
    } catch (err) {
      alert('Błąd usuwania: ' + err.message);
      console.error(err);
    }
  };

  const renderProgressBar = (level) => {
    const levels = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
    const idx = levels.indexOf(level?.toUpperCase?.() || 'A1');
    return (
      <div className="progress-bar">
        {levels.map((_, i) => (
          <div
            key={i}
            className={`progress-segment ${i <= idx ? 'orange' : 'gray'}`}
          />
        ))}
      </div>
    );
  };

  // Loader podczas 1 ładowania danych
  if (loading) {
    return <Loader />;
  }

  if (error) {
    return <div className="error">Błąd: {error}</div>;
  }

  return (
    <>
      <Helmet>
        <html lang="pl" />
        <title>
          Resume / CV - Mikołaj Melnyk | Web Developer React & Full-Stack
        </title>
        <meta
          name="description"
          content="Resume i doświadczenie zawodowe początkującego web developera – React, Next.js, Node.js, Express, Tailwind CSS, PostgreSQL, JavaScript, TypeScript. Projekty, umiejętności, edukacja i osiągnięcia programistyczne. Portfolio web developera."
        />
        <meta
          name="keywords"
          content="resume, cv, doświadczenie, web developer, portfolio, React, Next.js, Node.js, Express, Tailwind CSS, PostgreSQL, JavaScript, TypeScript, frontend, fullstack, programista, Rzeszów, junior developer"
        />
        <meta
          property="og:title"
          content="Resume / CV - Mikołaj Melnyk | React • Node.js • Full-Stack Developer"
        />
        <meta
          property="og:description"
          content="Przejrzyj moje doświadczenie, umiejętności techniczne, projekty i edukację. Specjalizacja w nowoczesnych technologiach webowych – React, Next.js, Node.js i więcej."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mikolajmelnyk.pl/Resume" />
        <meta property="og:locale" content="pl_PL" />
        <meta property="og:site_name" content="Portfolio Mikołaj Melnyk" />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Mikołaj Melnyk" />
        <meta name="revisit-after" content="7 days" />
      </Helmet>

      <div className="resume-container">
        <div className="content">
          <h1 className="title_h1">Resume</h1>

          {/* EDUKACJA */}
          <section className="education">
            <h2 className="subtitle_r">Edukacja</h2>
            {grouped.education?.length > 0 ? (
              grouped.education.map((item) => (
                <div key={item.id} className="education-item">
                  {item.iconUrl && (
                    <div className="col_photo">
                      <img
                        src={item.iconUrl}
                        alt={item.title}
                        className="logo"
                      />
                    </div>
                  )}
                  <div className="col_text">
                    <div className="editable-header">
                      <h3 className="subsubtitle_r">{item.title}</h3>
                      {isAuthenticated && (
                        <div className="action-buttons">
                          <button
                            className="edit-icon-btn small"
                            onClick={() => openModal(item, 'edit')}
                            title="Edytuj wpis"
                          >
                            <i className="fas fa-pencil-alt"></i>
                          </button>
                          <button
                            className="delete-icon-btn small"
                            onClick={() => handleDelete(item.id)}
                            title="Usuń wpis"
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      )}
                    </div>
                    {item.subtitle && (
                      <p className="subtitle-small">{item.subtitle}</p>
                    )}
                    <span className="date_r">
                      {item.startDate
                        ? new Date(item.startDate).getFullYear()
                        : '—'}{' '}
                      –{' '}
                      {item.endDate
                        ? new Date(item.endDate).getFullYear()
                        : 'obecnie'}
                    </span>
                    {item.description && renderMultilineText(item.description)}
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-section">
                <p>Brak wpisów w kategorii Edukacja</p>
              </div>
            )}

            {isAuthenticated && (
              <button
                className="add-btn"
                onClick={() => openModal(null, 'add', 'education')}
              >
                + Dodaj edukację
              </button>
            )}
          </section>

          {/* DOŚWIADCZENIE */}
          <section className="experience">
            <h2 className="subtitle_r">Doświadczenie</h2>
            {grouped.experience?.length > 0 ? (
              grouped.experience.map((item) => (
                <div key={item.id} className="experience-item">
                  <div className="col_text">
                    <div className="editable-header">
                      <h3 className="subsubtitle_r">{item.title}</h3>
                      {isAuthenticated && (
                        <div className="action-buttons-side">
                          <button
                            className="edit-icon-btn small"
                            onClick={() => openModal(item, 'edit')}
                            title="Edytuj wpis"
                          >
                            <i className="fas fa-pencil-alt"></i>
                          </button>
                          <button
                            className="delete-icon-btn small"
                            onClick={() => handleDelete(item.id)}
                            title="Usuń wpis"
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      )}
                    </div>
                    <span className="date_r">
                      {new Date(item.startDate).getFullYear()} –{' '}
                      {item.endDate
                        ? new Date(item.endDate).getFullYear()
                        : 'obecnie'}
                    </span>
                    {item.subtitle && (
                      <p className="position">{item.subtitle}</p>
                    )}
                    {item.description && renderMultilineText(item.description)}
                  </div>
                  {item.iconUrl && (
                    <div className="col_photo">
                      <img
                        src={item.iconUrl}
                        alt={item.title}
                        className="logo"
                      />
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="empty-section">
                <p>Brak wpisów w kategorii Doświadczenie</p>
              </div>
            )}

            {isAuthenticated && (
              <button
                className="add-btn"
                onClick={() => openModal(null, 'add', 'experience')}
              >
                + Dodaj doświadczenie
              </button>
            )}
          </section>

          {/* OSIĄGNIĘCIA */}
          <section className="achievement">
            <h2 className="subtitle_r">Osiągnięcia</h2>
            {grouped.achievements?.length > 0 ? (
              grouped.achievements.map((item) => (
                <div key={item.id} className="col_3_achievement">
                  {item.iconUrl && (
                    <div className="col_photo">
                      <img
                        src={item.iconUrl}
                        alt={item.title}
                        className="logo"
                      />
                    </div>
                  )}
                  <div className="col_text">
                    <div className="editable-header">
                      <h3 className="subsubtitle_r">{item.title}</h3>
                      {isAuthenticated && (
                        <div className="action-buttons vertical">
                          <button
                            className="edit-icon-btn small"
                            onClick={() => openModal(item, 'edit')}
                            title="Edytuj wpis"
                          >
                            <i className="fas fa-pencil-alt"></i>
                          </button>
                          <button
                            className="delete-icon-btn small"
                            onClick={() => handleDelete(item.id)}
                            title="Usuń wpis"
                          >
                            <i className="fas fa-trash-alt"></i>
                          </button>
                        </div>
                      )}
                    </div>
                    {item.subtitle && (
                      <p className="subtitle-small">{item.subtitle}</p>
                    )}
                    {item.description && renderMultilineText(item.description)}
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-section">
                <p>Brak wpisów w kategorii Osiągnięcia</p>
              </div>
            )}

            {isAuthenticated && (
              <button
                className="add-btn"
                onClick={() => openModal(null, 'add', 'achievements')}
              >
                + Dodaj osiągnięcie
              </button>
            )}
          </section>

          {/* Skills & Languages – dolna wersja na mobile */}
          <div className="title-aside-r-low">
            <h1>Skills & Language</h1>
          </div>

          <div className="aside-content-low">
            {/* Umiejętności */}
            <div className="skills">
              <h2>Umiejętności</h2>
              <div className="line"></div>
              <div className="skills-column">
                <div className="skill-col">
                  {(grouped.skills || []).map((item) => (
                    <div key={item.id} className="skill-row">
                      {item.iconUrl && (
                        <img
                          src={item.iconUrl}
                          alt={item.title}
                          className="min-image-skill"
                        />
                      )}
                      <li className="name-skill">{item.title}</li>
                    </div>
                  ))}
                </div>
              </div>
              {isAuthenticated && (
                <div className="admin-controls">
                  <button
                    className="add-btn"
                    onClick={() => openModal(null, 'add', 'skills')}
                  >
                    + Dodaj umiejętność
                  </button>

                  <button
                    className="delete-btn"
                    onClick={() => openModal(null, 'select-delete', 'skills')}
                  >
                    Usuń umiejętność
                  </button>
                </div>
              )}
            </div>

            {/* Języki */}
            <div className="lang">
              <h2>Znajomość języków</h2>
              <div className="line"></div>
              <div className="lang-column">
                <div className="name-lang">
                  {(grouped.languages || []).map((item) => (
                    <li key={item.id} className="left-li">
                      <strong>{item.title}:</strong> {item.level || '—'}
                      {renderProgressBar(item.level)}
                    </li>
                  ))}
                </div>
              </div>
              {isAuthenticated && (
                <div className="admin-controls">
                  <button
                    className="add-btn"
                    onClick={() => openModal(null, 'add', 'languages')}
                  >
                    + Dodaj język
                  </button>

                  <button
                    className="delete-btn"
                    onClick={() =>
                      openModal(null, 'select-delete', 'languages')
                    }
                  >
                    Usuń język
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Informacje dodatkowe */}
          <section className="info-section">
            <h2 className="subtitle_r">Informacje dodatkowe</h2>
            <div className="line"></div>

            <div className="info-item">
              {/* Lewa kolumna – Projekty */}
              <div className="left-info">
                <div className="col_photo6"></div>

                <div className="col_text_info">
                  <Link to="/Projects" className="subsubtitle_info">
                    <button className="contact-click-info">
                      Zobacz moje projekty{' '}
                      <i className="fas fa-arrow-right send-arrow"></i>
                    </button>
                  </Link>

                  <p className="info-desc">
                    Sprawdź moje najnowsze projekty – aplikacje webowe,
                    frontend, backend oraz rozwiązania full-stack.
                  </p>
                </div>
              </div>

              {/* Prawa kolumna – Kontakt */}
              <div className="right-info">
                <div className="col_photo7"></div>

                <div className="col_text_info">
                  <Link to="/Contact" className="subsubtitle_info">
                    <button className="contact-click-info">
                      Skontaktuj się ze mną{' '}
                      <i className="fas fa-arrow-right send-arrow"></i>
                    </button>
                  </Link>

                  <p className="info-desc">
                    Masz pomysł, pytanie lub propozycję współpracy? Napisz –
                    chętnie porozmawiamy i zobaczymy, co możemy razem stworzyć.
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Prawa kolumna – Skills & Languages (desktop) */}
        <div className="aside-content">
          <div className="title-aside-r">
            <h1>Umiejętności i Języki</h1>
          </div>

          {/* Umiejętności */}
          <div className="skills">
            <h2>Umiejętności</h2>
            <div className="line"></div>
            <div className="skills-column">
              <div className="skill-col">
                {(grouped.skills || []).map((item) => (
                  <div key={item.id} className="skill-row">
                    {item.iconUrl && (
                      <img
                        src={item.iconUrl}
                        alt={item.title}
                        className="min-image-skill"
                      />
                    )}
                    <li className="name-skill">{item.title}</li>
                  </div>
                ))}
              </div>
            </div>
            {isAuthenticated && (
              <div className="admin-controls">
                <button
                  className="add-btn"
                  onClick={() => openModal(null, 'add', 'skills')}
                >
                  + Dodaj umiejętność
                </button>

                <button
                  className="delete-btn"
                  onClick={() => openModal(null, 'select-delete', 'skills')}
                >
                  Usuń umiejętność
                </button>
              </div>
            )}
          </div>

          {/* Języki */}
          <div className="lang">
            <h2>Znajomość języków</h2>
            <div className="line"></div>
            <div className="lang-column">
              <div className="name-lang">
                {(grouped.languages || []).map((item) => (
                  <li key={item.id} className="left-li">
                    <strong>{item.title}:</strong> {item.level || '—'}
                    {renderProgressBar(item.level)}
                  </li>
                ))}
              </div>
            </div>
            {isAuthenticated && (
              <div className="admin-controls">
                <button
                  className="add-btn"
                  onClick={() => openModal(null, 'add', 'languages')}
                >
                  + Dodaj język
                </button>

                <button
                  className="delete-btn"
                  onClick={() => openModal(null, 'select-delete', 'languages')}
                >
                  Usuń język
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Modal */}
        {modalOpen && (
          <ResumeModal
            entry={editingEntry}
            onClose={closeModal}
            onSave={handleSave}
            entries={grouped[selectedCategory] || []}
            mode={modalMode}
            categoryForSelection={selectedCategory}
            onSelectEdit={(selectedEntry) => {
              openModal(selectedEntry, 'edit');
            }}
          />
        )}
      </div>
    </>
  );
};

export default Resume;
